import React, { useState, useEffect } from 'react';
import { TourInfo } from './types';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PlacesAutocompleteV1 } from '@/components/ui/places-autocomplete-v1';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  ImagePlus, 
  Wand2, 
  Map, 
  Trash, 
  ChevronDown, 
  ChevronRight,
  Calendar,
  Clock,
  MapPin,
  DollarSign,
  Loader2,
  Tag,
  FileText,
  Image,
  AlignLeft,
  CircleDot,
  ClipboardList,
  Users,
  Info,
  Briefcase,
  Bookmark,
  MoreHorizontal,
  Copy,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ImageManagerModal } from '@/components/ui/image-manager-modal';
import { useOpenAiApi } from '@/hooks/useOpenAiApi';
import { useToast } from '@/hooks/use-toast';
import { SaveToLibraryModal } from './SaveToLibraryModal';
import { isItemSavedInLibrary } from '@/lib/library-utils';
import { usePrompt } from '@/contexts/PromptContext';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { normalizeLocationData } from '@/lib/coordinates-utils';

interface TourFormProps {
  tour: TourInfo;
  index: number;
  pageId: string;
  updateTour: (pageId: string, tourId: string, field: keyof TourInfo, value: any) => void;
  batchUpdateTour?: (tourId: string, updates: Partial<TourInfo>) => void;
  deleteTour: (pageId: string, tourId: string) => void;
  duplicateTour?: (pageId: string, tourId: string) => void;
  moveTourToPage?: (tourId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

// Custom styled label with orange underline and icon
const StyledLabel = ({ 
  children, 
  htmlFor, 
  className, 
  icon: Icon = CircleDot,
  ...props 
}: React.ComponentProps<typeof Label> & { icon?: React.ElementType }) => (
  <Label 
    htmlFor={htmlFor}
    className={cn("border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left text-sm font-medium text-gray-700", className)}
    {...props}
  >
    <Icon className="h-3.5 w-3.5 text-voah-coral flex-shrink-0" />
    <span className="whitespace-nowrap">{children}</span>
  </Label>
);

// Update input styles by creating a component wrapper
const FormInput = ({ className, ...props }: React.ComponentProps<typeof Input>) => (
  <Input
    className={cn("text-sm text-gray-600 font-normal", className)}
    {...props}
  />
);

const FormTextarea = ({ className, ...props }: React.ComponentProps<typeof Textarea>) => (
  <Textarea
    className={cn("text-sm text-gray-600 font-normal", className)}
    {...props}
  />
);

export function TourForm({
  tour,
  index,
  pageId,
  updateTour,
  batchUpdateTour,
  deleteTour,
  duplicateTour,
  moveTourToPage,
  isCollapsed,
  onToggleCollapse
}: TourFormProps) {
  const { toast } = useToast();
  const { chatCompletion } = useOpenAiApi();
  const tourDescriptionPrompt = usePrompt('tour_description');
  const enhanceDescriptionPrompt = usePrompt('enhance_description');
  
  const [isImageManagerOpen, setIsImageManagerOpen] = useState(false);
  const [isProcessingDescription, setIsProcessingDescription] = useState(false);
  const [isProcessingNameToDescription, setIsProcessingNameToDescription] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSaveToLibraryOpen, setIsSaveToLibraryOpen] = useState(false);
  const [isSavedToLibrary, setIsSavedToLibrary] = useState(false);
  const [tourTitle, setTourTitle] = useState<string>(() => {
    // Initialize title based on current tour
    if (tour.name) {
      return tour.name;
    } else {
      return `Passeio #${index + 1}`;
    }
  });
  // Track if location differs from title for sync icon
  const [locationDiffersFromTitle, setLocationDiffersFromTitle] = useState(false);

  // Debug effect to track tour changes
  useEffect(() => {
    console.log("[TourForm] Tour updated:", {
      id: tour.id,
      location: tour.location,
      coordinates: tour.coordinates
    });
  }, [tour.location, tour.coordinates]);

  // Effect to keep tourTitle in sync with tour.name
  useEffect(() => {
    // Usado apenas para visualização na UI
    setTourTitle(tour.name || `Passeio #${index + 1}`);
  }, [tour.name, index]);

  // Effect to check if location differs from title
  useEffect(() => {
    setLocationDiffersFromTitle(
      !!tour.name && 
      (tour.location === '' || 
       tour.location === undefined || 
       tour.name !== tour.location)
    );
  }, [tour.name, tour.location]);

  // Verificar se o item já está salvo na biblioteca
  useEffect(() => {
    let isMounted = true;
    
    const checkLibraryStatus = async () => {
      try {
        const isSaved = await isItemSavedInLibrary(tour, 'tour');
        if (isMounted) {
          setIsSavedToLibrary(isSaved);
        }
      } catch (error) {
        console.error('Erro ao verificar status na biblioteca:', error);
        if (isMounted) {
          setIsSavedToLibrary(false);
        }
      }
    };
    
    checkLibraryStatus();
    
    return () => {
      isMounted = false; // cleanup to prevent setting state on unmounted component
    };
  }, [tour]);

  // Log para verificar se batchUpdateTour está disponível
  console.log("[TourForm] batchUpdateTour is available:", {
    hasBatchUpdateTour: !!batchUpdateTour,
    batchUpdateTourType: typeof batchUpdateTour,
    tourId: tour.id
  });

  // Function to update a field
  const handleUpdate = (field: keyof TourInfo, value: any) => {
    let safeValue = value;
    
    // Special handling for coordinates to ensure they're properly formatted
    if (field === 'coordinates' && value) {
      const lat = typeof value.lat === 'string' ? parseFloat(value.lat) : Number(value.lat);
      const lng = typeof value.lng === 'string' ? parseFloat(value.lng) : Number(value.lng);
      
      safeValue = {
        lat: isNaN(lat) ? 0 : lat,
        lng: isNaN(lng) ? 0 : lng
      };
    }
    
    updateTour(pageId, tour.id, field, safeValue);
  };

  // AI Description enhancement
  const handleProcessDescription = async () => {
    if (!tour.description || tour.description.trim() === '') {
      toast({
        title: "Descrição vazia",
        description: "Por favor, insira um texto na descrição para ser processado pela IA.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessingDescription(true);
    
    try {
      // Extract text from HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = tour.description;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';

      // Use the prompt from context or fall back to default
      const defaultEnhancePrompt = `Por favor, melhore a seguinte descrição de passeio ou atividade turística, mantendo todas as informações importantes, mas tornando-a mais atraente, clara e profissional:\n\n{description}`;
      const promptTemplate = enhanceDescriptionPrompt || defaultEnhancePrompt;
      
      // Replace the description placeholder
      const prompt = promptTemplate.replace(/{description}/g, textContent);
      
      // Increase maxTokens to avoid truncation
      const response = await chatCompletion(prompt, 'gpt-4-turbo', 0.7, 1500);
      
      if (response && response.choices && response.choices.length > 0) {
        // Extrair o conteúdo da mensagem da resposta
        const generatedContent = response.choices[0].message.content.trim();
        
        // Check if the response was truncated
        if (response.choices[0].finish_reason === 'length') {
          toast({
            title: "Aviso de truncamento",
            description: "O texto gerado foi truncado devido ao limite de tamanho. Considere dividir o conteúdo em partes menores.",
            variant: "default"
          });
        }
        
        // Atualizar o campo de descrição com o conteúdo extraído
        handleUpdate('description', generatedContent);
        
        toast({
          title: "Descrição processada",
          description: "A descrição foi melhorada com sucesso pela IA.",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Erro ao processar descrição:', error);
      toast({
        title: "Erro",
        description: "Houve um erro ao processar a descrição. Por favor, tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsProcessingDescription(false);
    }
  };

  // AI Name to Description generator
  const handleProcessNameToDescription = async () => {
    if (!tour.name || tour.name.trim() === '') {
      toast({
        title: "Nome vazio",
        description: "Por favor, insira um nome para o passeio para gerar uma descrição.",
        variant: "destructive"
      });
      return;
    }

    const tourName = tour.name.trim();
    setIsProcessingNameToDescription(true);
    
    try {
      toast({
        title: "Gerando descrição",
        description: `Gerando descrição para "${tourName}"...`,
      });
      
      // Use the prompt from context or fall back to default
      const defaultTourPrompt = `Escreva uma descrição detalhada de um passeio turístico chamado "{tourName}". 
      A descrição deve ter entre 150 e 300 palavras.`;
      
      const promptTemplate = tourDescriptionPrompt || defaultTourPrompt;
      const prompt = promptTemplate.replace(/{tourName}/g, tourName);
      
      // Increase maxTokens to avoid truncation
      const response = await chatCompletion(prompt, 'gpt-4-turbo', 0.7, 1500);
      
      if (response && response.choices && response.choices.length > 0) {
        // Extrair o conteúdo da mensagem da resposta
        const generatedContent = response.choices[0].message.content.trim();
        
        // Check if the response was truncated
        if (response.choices[0].finish_reason === 'length') {
          toast({
            title: "Aviso de truncamento",
            description: "O texto gerado foi truncado devido ao limite de tamanho. Considere dividir o conteúdo em partes menores.",
            variant: "default"
          });
        }
        
        // Atualizar o campo de descrição com o conteúdo extraído
        handleUpdate('description', generatedContent);
        
        toast({
          title: "Descrição gerada",
          description: "Uma descrição para o passeio foi gerada com sucesso.",
        });
      }
    } catch (error) {
      console.error('Erro ao gerar descrição com IA:', error);
      
      toast({
        title: "Erro na geração",
        description: error instanceof Error ? error.message : "Ocorreu um erro ao gerar a descrição com IA.",
        variant: "destructive"
      });
    } finally {
      setIsProcessingNameToDescription(false);
    }
  };

  // Format currency values
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Parse currency input
  const parseCurrency = (value: string): number | null => {
    if (!value) return null;
    const numericValue = value.replace(/\D/g, '');
    return numericValue ? Number(numericValue) / 100 : null;
  };

  // Handler para o botão de salvar na biblioteca
  const handleSaveToLibrary = (e: React.MouseEvent) => {
    e.stopPropagation(); // Impedir que o clique colapse/expanda o item
    setIsSaveToLibraryOpen(true);
  };

  // Finalizar edição do título
  const handleFinishTitleEdit = () => {
    setIsEditingTitle(false);
    
    // Se o título não estiver vazio
    if (tourTitle.trim() !== '') {
      handleUpdate('name', tourTitle.trim());
    } else {
      // Se estiver vazio, defina o campo name como vazio
      handleUpdate('name', '');
    }
  };

  const handleSelectPlace = (place: any) => {
    if (place) {
      console.log('[TourForm] Original place data:', place);
      
      // Use the normalizeLocationData utility to ensure consistent format
      const normalizedPlace = normalizeLocationData(place);
      
      // Skip if normalization failed
      if (!normalizedPlace) {
        console.error('[TourForm] Failed to normalize place data:', place);
        toast({
          title: "Erro ao processar local",
          description: "Não foi possível processar os dados do local selecionado.",
          variant: "destructive"
        });
        return;
      }
      
      // Create an updates object with normalized location data
      const updates: Partial<TourInfo> = {
        location: normalizedPlace.name,
        placeId: normalizedPlace.placeId,
        coordinates: normalizedPlace.coordinates ? {
          lat: Number(normalizedPlace.coordinates.lat),
          lng: Number(normalizedPlace.coordinates.lng)
        } : undefined,
        googleMapsUrl: normalizedPlace.googleMapsUrl,
        city: normalizedPlace.city,
        country: normalizedPlace.country
      };
      
      console.log('[TourForm] Processing place data:', {
        normalizedPlace,
        updates,
        hasBatchUpdateTour: !!batchUpdateTour,
        coordinatesAvailable: !!normalizedPlace.coordinates
      });
      
      // Aplicar as atualizações usando batchUpdateTour se disponível
      if (batchUpdateTour) {
        console.log('[TourForm] Using batchUpdateTour to update tour:', {
          tourId: tour.id,
          updatesHasCoordinates: !!updates.coordinates,
          coordinates: updates.coordinates
        });
        
        batchUpdateTour(tour.id, updates);
        
        // Show toast only after successful update
        setTimeout(() => {
          toast({
            title: "Local adicionado",
            description: `${normalizedPlace.name} foi vinculado a este passeio.`,
          });
        }, 300);
      } else {
        // Fallback para o método anterior se batchUpdateTour não estiver disponível
        Object.entries(updates).forEach(([key, value]) => {
          updateTour(pageId, tour.id, key as keyof TourInfo, value);
        });
        // Show toast only after successful update
        setTimeout(() => {
          toast({
            title: "Local adicionado",
            description: `${normalizedPlace.name} foi vinculado a este passeio.`,
          });
        }, 300);
      }
    }
  };

  // Function to sync location with title
  const handleSyncLocationWithTitle = () => {
    if (tour.name) {
      handleUpdate('location', tour.name);
    }
  };

  return (
    <>
      <Card className={cn(
        "border shadow-sm transition-all border-l-4 border-l-amber-600",
        isCollapsed && "border-transparent border-l-amber-600"
      )}>
        <CardHeader 
          className={cn(
            "cursor-pointer flex items-center justify-between",
            isCollapsed ? "py-2 px-3" : "py-3 px-4"
          )}
          onClick={onToggleCollapse}
        >
          <div className="flex items-center gap-2 w-full">
            <Map className="h-4 w-4 text-amber-600" />
            
            {isEditingTitle ? (
              <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                <Input
                  value={tourTitle}
                  onChange={(e) => setTourTitle(e.target.value)}
                  className="h-7 mr-2 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-transparent"
                  autoFocus
                  onFocus={() => {
                    // Se for apenas o placeholder, limpe o campo para edição
                    if (tourTitle.match(/^Passeio #\d+$/)) {
                      setTourTitle('');
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleFinishTitleEdit();
                    }
                  }}
                  onBlur={handleFinishTitleEdit}
                />
              </div>
            ) : (
              <>
                {isCollapsed ? (
                  <span className="font-medium">{tourTitle}</span>
                ) : (
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingTitle(true);
                      // Se for apenas o placeholder, limpe o campo para edição
                      if (tourTitle.match(/^Passeio #\d+$/)) {
                        setTourTitle('');
                      }
                    }}
                  >
                    <h3 className="font-medium">{tourTitle}</h3>
                  </div>
                )}
              </>
            )}
            
            {/* Quick info for collapsed view */}
            {isCollapsed && (
              <>
                {tour.location && (
                  <div className="flex items-center ml-3 text-sm text-gray-500">
                    <Map className="h-3.5 w-3.5 mr-1 text-amber-600" />
                    <span>{tour.location.substring(0, 20)}{tour.location.length > 20 ? '...' : ''}</span>
                  </div>
                )}
                
                {tour.date && (
                  <div className="flex items-center ml-3 text-sm text-gray-500">
                    <Calendar className="h-3.5 w-3.5 mr-1" />
                    <span>{tour.date}</span>
                  </div>
                )}
                
                {tour.time && (
                  <div className="flex items-center ml-3 text-sm text-gray-500">
                    <Clock className="h-3.5 w-3.5 mr-1" />
                    <span>{tour.time}</span>
                  </div>
                )}

                {tour.price !== undefined && tour.price !== null && (
                  <div className="flex items-center ml-3 text-sm text-gray-500">
                    <DollarSign className="h-3.5 w-3.5 mr-1" />
                    <span>{formatCurrency(tour.price)} {tour.currency || 'BRL'}</span>
                  </div>
                )}
              </>
            )}
            
            <div className="flex items-center ml-auto">
              {/* Botão de Salvar na Biblioteca (bookmark) */}
              <Button
                variant={isSavedToLibrary ? "default" : "ghost"}
                size="icon"
                className={cn(
                  "h-8 w-8 transition-colors",
                  isSavedToLibrary 
                    ? "bg-amber-500 hover:bg-amber-600" 
                    : "hover:text-amber-500"
                )}
                onClick={handleSaveToLibrary}
                title={isSavedToLibrary ? "Item salvo na biblioteca" : "Salvar na Biblioteca"}
              >
                <Bookmark className={cn(
                  "h-4 w-4", 
                  isSavedToLibrary 
                    ? "text-white" 
                    : "text-gray-500 hover:text-amber-500"
                )} />
              </Button>
              
              {/* Dropdown menu with actions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4 text-gray-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[180px]">
                  {duplicateTour && (
                    <DropdownMenuItem 
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateTour(pageId, tour.id);
                      }}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      <span>Duplicar</span>
                    </DropdownMenuItem>
                  )}
                  
                  {moveTourToPage && (
                    <DropdownMenuItem 
                      onClick={(e) => {
                        e.stopPropagation();
                        moveTourToPage(tour.id);
                      }}
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />
                      <span>Mover</span>
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteTour(pageId, tour.id);
                    }}
                    className="text-red-600"
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    <span>Excluir</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </div>
          </div>
        </CardHeader>

        {!isCollapsed && (
          <CardContent className="p-4 pt-0 space-y-4">
            {/* Name and Location fields side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`name-${tour.id}`}
                    icon={Tag}
                  >
                    Nome do Passeio
                  </StyledLabel>
                </div>
                <div className="relative w-full">
                  <FormInput
                    id={`name-${tour.id}`}
                    value={tourTitle}
                    onChange={(e) => setTourTitle(e.target.value)}
                    onFocus={() => {
                      // Se for apenas o placeholder, limpe o campo para edição
                      if (tourTitle.match(/^Passeio #\d+$/)) {
                        setTourTitle('');
                      }
                    }}
                    onBlur={handleFinishTitleEdit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleFinishTitleEdit();
                      }
                    }}
                    placeholder="Nome do passeio ou atividade"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-voah-coral hover:text-white hover:bg-voah-coral"
                    onClick={handleProcessNameToDescription}
                    disabled={isProcessingNameToDescription || !tour.name}
                    title="Gerar descrição a partir do nome"
                  >
                    {isProcessingNameToDescription ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wand2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Location with Google Places integration */}
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`location-${tour.id}`}
                    icon={MapPin}
                  >
                    Local
                  </StyledLabel>
                </div>
                <div className="relative">
                  <PlacesAutocompleteV1
                    value={tour.location || ''}
                    onChange={(value) => handleUpdate('location', value)}
                    onPlaceSelect={handleSelectPlace}
                    placeholder="Digite um local"
                    className="w-full"
                    showNameInsteadOfAddress={true}
                  />
                  {locationDiffersFromTitle && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-10 top-0 h-10 w-10"
                      onClick={handleSyncLocationWithTitle}
                      title="Sincronizar localização com o título"
                    >
                      <RefreshCw className="h-4 w-4 text-gray-500" />
                    </Button>
                  )}
                </div>
                
                {/* Status das coordenadas */}
                <div className="mt-1 flex items-center text-xs">
                  {tour.coordinates ? (
                    <div className="flex items-center text-green-600">
                      <div className="h-2 w-2 rounded-full bg-green-600 mr-1" />
                      <span>Coordenadas presentes: ({tour.coordinates.lat.toFixed(6)}, {tour.coordinates.lng.toFixed(6)})</span>
                    </div>
                  ) : tour.location ? (
                    <div className="flex items-center text-amber-600">
                      <div className="h-2 w-2 rounded-full bg-amber-600 mr-1" />
                      <span>Sem coordenadas. Selecione um local novamente.</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            
            {/* Description with rich text editor */}
            <div className="space-y-2 flex flex-col">
              <div className="flex items-center justify-between">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`description-${tour.id}`}
                    icon={AlignLeft}
                  >
                    Descrição
                  </StyledLabel>
                </div>
              </div>
              <RichTextEditor
                value={tour.description || ''}
                onChange={(value) => handleUpdate('description', value)}
                placeholder="Descreva o passeio..."
                onProcessWithAI={handleProcessDescription}
                isProcessing={isProcessingDescription}
              />
            </div>
            
            {/* Date and Time fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`date-${tour.id}`}
                    icon={Calendar}
                  >
                    Data
                  </StyledLabel>
                </div>
                <FormInput
                  type="date"
                  id={`date-${tour.id}`}
                  value={tour.date || ''}
                  onChange={(e) => handleUpdate('date', e.target.value)}
                />
              </div>
              
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`time-${tour.id}`}
                    icon={Clock}
                  >
                    Horário
                  </StyledLabel>
                </div>
                <FormInput
                  type="time"
                  id={`time-${tour.id}`}
                  value={tour.time || ''}
                  onChange={(e) => handleUpdate('time', e.target.value)}
                />
              </div>
            </div>
            
            {/* Duration */}
            <div className="space-y-2 flex flex-col">
              <div className="flex flex-col items-start">
                <StyledLabel
                  htmlFor={`duration-${tour.id}`}
                  icon={Clock}
                >
                  Duração
                </StyledLabel>
              </div>
              <FormInput
                id={`duration-${tour.id}`}
                value={tour.duration || ''}
                onChange={(e) => handleUpdate('duration', e.target.value)}
                placeholder="Ex: 2 horas"
              />
            </div>
            
            {/* Price section */}
            <div className="space-y-3 pt-2 border-t">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 flex flex-col">
                  <div className="flex flex-col items-start">
                    <StyledLabel
                      htmlFor={`price-${tour.id}`}
                      icon={DollarSign}
                    >
                      Preço
                    </StyledLabel>
                  </div>
                  <div className="relative">
                    <DollarSign className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-500" />
                    <FormInput
                      id={`price-${tour.id}`}
                      value={tour.price ? formatCurrency(tour.price) : ''}
                      onChange={(e) => {
                        const parsed = parseCurrency(e.target.value);
                        handleUpdate('price', parsed);
                      }}
                      className="pl-8"
                      placeholder="0,00"
                    />
                  </div>
                </div>
                <div className="space-y-2 flex flex-col">
                  <div className="flex flex-col items-start">
                    <StyledLabel
                      htmlFor={`currency-${tour.id}`}
                      icon={Tag}
                    >
                      Moeda
                    </StyledLabel>
                  </div>
                  <FormInput
                    id={`currency-${tour.id}`}
                    value={tour.currency || 'BRL'}
                    onChange={(e) => handleUpdate('currency', e.target.value)}
                    placeholder="BRL"
                  />
                </div>
              </div>
            </div>
            
            {/* Image management */}
            <div className="pt-4 border-t space-y-2">
              <div className="flex justify-between items-center">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    className="text-sm font-medium"
                    icon={Image}
                  >
                    Imagens
                  </StyledLabel>
                </div>
                <div className="flex items-center gap-2">
                  {tour.images && tour.images.length > 0 ? (
                    <div className="flex items-center gap-1">
                      <div 
                        className="h-8 w-8 rounded-md overflow-hidden border border-gray-200 shadow-sm hover:border-amber-500 cursor-pointer transition-all hover:scale-105"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsImageManagerOpen(true);
                        }}
                      >
                        <img 
                          src={tour.images[0]} 
                          alt="Miniatura" 
                          className="h-full w-full object-cover"
                        />
                      </div>
                      {tour.images.length > 1 && (
                        <span 
                          className="text-xs text-gray-500 cursor-pointer hover:text-amber-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsImageManagerOpen(true);
                          }}
                        >
                          +{tour.images.length - 1}
                        </span>
                      )}
                    </div>
                  ) : null}
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsImageManagerOpen(true);
                    }}
                    className="h-8 bg-white border-gray-200 hover:bg-gray-50 text-amber-500"
                  >
                    <ImagePlus className="h-4 w-4 mr-2" />
                    <span>Gerenciar imagens</span>
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Notes Section */}
            <div className="mt-4 pt-4 border-t">
              <div className="flex justify-between items-center mb-2">
                <StyledLabel htmlFor={`notes-${tour.id}`} icon={FileText}>Notas</StyledLabel>
              </div>
              <FormTextarea 
                id={`notes-${tour.id}`}
                value={tour.notes || ''} 
                onChange={(e) => handleUpdate('notes', e.target.value)}
                placeholder="Informações adicionais sobre o passeio..." 
                className="min-h-[100px]"
              />
            </div>
          </CardContent>
        )}
      </Card>

      {/* Modal para Salvar na Biblioteca */}
      {isSaveToLibraryOpen && (
        <SaveToLibraryModal
          isOpen={isSaveToLibraryOpen}
          onClose={() => setIsSaveToLibraryOpen(false)}
          onSave={(savedItem) => {
            setIsSavedToLibrary(true);
            toast({
              title: "Item salvo na biblioteca",
              description: "O passeio foi salvo com sucesso na sua biblioteca.",
            });
            setIsSaveToLibraryOpen(false);
          }}
          item={tour}
          itemType="tour"
        />
      )}
      
      {isImageManagerOpen && (
        <ImageManagerModal
          isOpen={isImageManagerOpen}
          onClose={() => setIsImageManagerOpen(false)}
          images={tour.images || []}
          onImagesChange={(images) => {
            handleUpdate('images', images);
          }}
          contextId={tour.id}
          contextType="tour"
        />
      )}
    </>
  );
} 